/**
 * Data Quality Wizard Component
 *
 * Shows positions missing phases, timelines, and quantities when clicking completion percentage
 */

import React, { useMemo } from 'react'
import { Modal, Header, List, Icon, Button, Divider, Segment } from 'semantic-ui-react'

interface PositionIssue {
  position_name: string
  type: 'internal' | 'external'
  key?: string
  quantity?: number
  phase_name?: string
  phase_id?: string
  start_date?: string
  end_date?: string
  unit_name?: string
  category_name?: string
  id?: string  // Added for deduplication
}

/**
 * Formats position display name with key for internal positions
 * @param position - Position issue object
 * @returns Formatted display name
 */
const formatPositionName = (position: PositionIssue): string => {
  if (position.type === 'internal' && position.key) {
    return `${position.key} - ${position.position_name}`
  }
  return position.position_name
}

interface DataQualityWizardProps {
  open: boolean
  onClose: () => void
  positionsWithoutPhases: PositionIssue[]
  invalidQuantityPositions: PositionIssue[]
  positionsWithoutTimelines: PositionIssue[]
  completionPercentage: number
  totalEnabledPositions: number
}

/**
 * Renders wizard dialog with data quality issues
 */
const DataQualityWizard: React.FC<DataQualityWizardProps> = ({
  open,
  onClose,
  positionsWithoutPhases,
  invalidQuantityPositions,
  positionsWithoutTimelines,
  completionPercentage,
  totalEnabledPositions
}) => {
  // Merge positions without phases and without timelines, removing duplicates
  const phaseTimelineIssues = useMemo(() => {
    const merged = new Map<string, PositionIssue>()

    // Add ALL positions from both arrays, using id as unique key
    // This ensures positions with EITHER issue show up

    // First, add positions without phases
    positionsWithoutPhases.forEach(pos => {
      const key = (pos as any).id || `${pos.type}-${pos.position_name}-${pos.key || ''}`
      merged.set(key, { ...(pos as any) })
    })

    // Then add/merge positions without timelines
    positionsWithoutTimelines.forEach(pos => {
      const key = (pos as any).id || `${pos.type}-${pos.position_name}-${pos.key || ''}`
      const existing = merged.get(key)

      if (existing) {
        // Position is in both arrays - merge the data
        merged.set(key, { ...existing, ...(pos as any) })
      } else {
        // Position only missing timelines, not phases - add it
        merged.set(key, { ...(pos as any) })
      }
    })

    const result = Array.from(merged.values())

    return result
  }, [positionsWithoutPhases, positionsWithoutTimelines])

  // Group phase/timeline issues by type
  const phaseTimelineByType = useMemo(() => {
    const internal = phaseTimelineIssues.filter(p => p.type === 'internal')
    const external = phaseTimelineIssues.filter(p => p.type === 'external')
    return { internal, external }
  }, [phaseTimelineIssues])

  // Group invalid quantities by type
  const invalidQtyByType = useMemo(() => {
    const internal = invalidQuantityPositions.filter(p => p.type === 'internal')
    const external = invalidQuantityPositions.filter(p => p.type === 'external')
    return { internal, external }
  }, [invalidQuantityPositions])

  const hasIssues = phaseTimelineIssues.length > 0 || invalidQuantityPositions.length > 0

  return (
    <Modal
      className="budget-modal" open={open} onClose={onClose} size="small">
      <Modal.Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: `conic-gradient(rgba(8, 214, 161, 1) ${completionPercentage * 3.6}deg, rgba(190, 200, 195, 1) 0deg)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'white'
            }} />
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#08D6A1' }}>
              {completionPercentage}% Complete
            </div>
            <div style={{ fontSize: '13px', color: '#747678', marginTop: '2px' }}>
              {totalEnabledPositions} Enabled Position{totalEnabledPositions !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Content scrolling>
        {!hasIssues ? (
          <Segment placeholder>
            <Header icon>
              <Icon name="check circle" color="green" />
              All data is complete!
            </Header>
            <p>All {totalEnabledPositions} enabled position{totalEnabledPositions !== 1 ? 's have' : ' has'} phases, timelines, and valid quantities.</p>
          </Segment>
        ) : (
          <>
            {/* Merged Phase/Timeline Issues Section */}
            {phaseTimelineIssues.length > 0 && (
              <>
                <Header as="h4" style={{ color: '#172533', marginBottom: '12px' }}>
                  <Icon name="calendar outline" color="orange" />
                  Missing Phase/Timeline Configuration ({phaseTimelineIssues.length})
                </Header>
                <p style={{ color: '#747678', fontSize: '13px', marginBottom: '12px' }}>
                  These positions are missing phase assignment or timeline dates:
                </p>

                {/* Direct Costs Subsection */}
                {phaseTimelineByType.internal.length > 0 && (
                  <Segment style={{
                    marginTop: '12px',
                    marginBottom: '12px',
                    border: '1px solid #E8EBED',
                    borderRadius: '6px',
                    padding: '0'
                  }}>
                    <Header
                      as="h5"
                      style={{
                        background: 'linear-gradient(to right, #F4F7F9, #FFFFFF)',
                        color: '#172533',
                        margin: '0',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #E8EBED',
                        borderRadius: '6px 6px 0 0'
                      }}
                    >
                      <Icon name="briefcase" style={{ color: '#08D6A1' }} />
                      Direct Costs ({phaseTimelineByType.internal.length})
                    </Header>
                    <div style={{ padding: '8px 16px' }}>
                      <List divided relaxed>
                        {phaseTimelineByType.internal.map((position, idx) => {
                          const missingPhase = !position.phase_id || position.phase_id === ''
                          const missingDates = !position.start_date || !position.end_date ||
                                              position.start_date === '' || position.end_date === ''

                          return (
                            <List.Item key={idx}>
                              <List.Content>
                                <List.Header style={{ color: '#172533' }}>
                                  {formatPositionName(position)}
                                </List.Header>
                                <List.Description style={{ color: '#747678', fontSize: '12px' }}>
                                  {position.category_name && <div>Category: {position.category_name}</div>}
                                  {position.unit_name && <div>Unit: {position.unit_name}</div>}
                                  {missingPhase && (
                                    <div style={{ color: '#E07B39', marginTop: '4px' }}>
                                      <Icon name="warning sign" size="small" /> No phase assigned
                                    </div>
                                  )}
                                  {missingDates && (
                                    <div style={{ color: '#E07B39', marginTop: '4px' }}>
                                      <Icon name="clock outline" size="small" /> Missing start/end dates
                                    </div>
                                  )}
                                </List.Description>
                              </List.Content>
                            </List.Item>
                          )
                        })}
                      </List>
                    </div>
                  </Segment>
                )}

                {/* Pass-Through Costs Subsection */}
                {phaseTimelineByType.external.length > 0 && (
                  <Segment style={{
                    marginTop: '12px',
                    marginBottom: '12px',
                    border: '1px solid #E8EBED',
                    borderRadius: '6px',
                    padding: '0'
                  }}>
                    <Header
                      as="h5"
                      style={{
                        background: 'linear-gradient(to right, #FFF9F4, #FFFFFF)',
                        color: '#172533',
                        margin: '0',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #E8EBED',
                        borderRadius: '6px 6px 0 0'
                      }}
                    >
                      <Icon name="handshake outline" style={{ color: '#E07B39' }} />
                      Pass-Through Costs ({phaseTimelineByType.external.length})
                    </Header>
                    <div style={{ padding: '8px 16px' }}>
                      <List divided relaxed>
                        {phaseTimelineByType.external.map((position, idx) => {
                          const missingPhase = !position.phase_id || position.phase_id === ''
                          const missingDates = !position.start_date || !position.end_date ||
                                              position.start_date === '' || position.end_date === ''

                          return (
                            <List.Item key={idx}>
                              <List.Content>
                                <List.Header style={{ color: '#172533' }}>
                                  {formatPositionName(position)}
                                </List.Header>
                                <List.Description style={{ color: '#747678', fontSize: '12px' }}>
                                  {position.unit_name && <div>Unit: {position.unit_name}</div>}
                                  {missingPhase && (
                                    <div style={{ color: '#E07B39', marginTop: '4px' }}>
                                      <Icon name="warning sign" size="small" /> No phase assigned
                                    </div>
                                  )}
                                  {missingDates && (
                                    <div style={{ color: '#E07B39', marginTop: '4px' }}>
                                      <Icon name="clock outline" size="small" /> Missing start/end dates
                                    </div>
                                  )}
                                </List.Description>
                              </List.Content>
                            </List.Item>
                          )
                        })}
                      </List>
                    </div>
                  </Segment>
                )}
                <Divider />
              </>
            )}

            {/* Invalid Quantities Section */}
            {invalidQuantityPositions.length > 0 && (
              <>
                <Header as="h4" style={{ color: '#172533', marginBottom: '12px' }}>
                  <Icon name="warning sign" color="red" />
                  Invalid Quantities ({invalidQuantityPositions.length})
                </Header>
                <p style={{ color: '#747678', fontSize: '13px', marginBottom: '12px' }}>
                  These positions have quantity = 0 or missing:
                </p>

                {/* Direct Costs Subsection */}
                {invalidQtyByType.internal.length > 0 && (
                  <Segment style={{
                    marginTop: '12px',
                    marginBottom: '12px',
                    border: '1px solid #E8EBED',
                    borderRadius: '6px',
                    padding: '0'
                  }}>
                    <Header
                      as="h5"
                      style={{
                        background: 'linear-gradient(to right, #F4F7F9, #FFFFFF)',
                        color: '#172533',
                        margin: '0',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #E8EBED',
                        borderRadius: '6px 6px 0 0'
                      }}
                    >
                      <Icon name="briefcase" style={{ color: '#08D6A1' }} />
                      Direct Costs ({invalidQtyByType.internal.length})
                    </Header>
                    <div style={{ padding: '8px 16px' }}>
                      <List divided relaxed>
                        {invalidQtyByType.internal.map((position, idx) => (
                          <List.Item key={idx}>
                            <List.Content>
                              <List.Header style={{ color: '#172533' }}>
                                {formatPositionName(position)}
                              </List.Header>
                              <List.Description style={{ color: '#747678', fontSize: '12px' }}>
                                <div>Quantity: {position.quantity ?? 'Not set'}</div>
                                {position.phase_name && <div>Phase: {position.phase_name}</div>}
                                {position.category_name && <div>Category: {position.category_name}</div>}
                              </List.Description>
                            </List.Content>
                          </List.Item>
                        ))}
                      </List>
                    </div>
                  </Segment>
                )}

                {/* Pass-Through Costs Subsection */}
                {invalidQtyByType.external.length > 0 && (
                  <Segment style={{
                    marginTop: '12px',
                    marginBottom: '12px',
                    border: '1px solid #E8EBED',
                    borderRadius: '6px',
                    padding: '0'
                  }}>
                    <Header
                      as="h5"
                      style={{
                        background: 'linear-gradient(to right, #FFF9F4, #FFFFFF)',
                        color: '#172533',
                        margin: '0',
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        borderBottom: '1px solid #E8EBED',
                        borderRadius: '6px 6px 0 0'
                      }}
                    >
                      <Icon name="handshake outline" style={{ color: '#E07B39' }} />
                      Pass-Through Costs ({invalidQtyByType.external.length})
                    </Header>
                    <div style={{ padding: '8px 16px' }}>
                      <List divided relaxed>
                        {invalidQtyByType.external.map((position, idx) => (
                          <List.Item key={idx}>
                            <List.Content>
                              <List.Header style={{ color: '#172533' }}>
                                {formatPositionName(position)}
                              </List.Header>
                              <List.Description style={{ color: '#747678', fontSize: '12px' }}>
                                <div>Quantity: {position.quantity ?? 'Not set'}</div>
                                {position.phase_name && <div>Phase: {position.phase_name}</div>}
                              </List.Description>
                            </List.Content>
                          </List.Item>
                        ))}
                      </List>
                    </div>
                  </Segment>
                )}
              </>
            )}
          </>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose} primary>
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default DataQualityWizard
